/**
 * Save a text file.
 * Desktop browsers download an anchor whose download name and blob type
 * keep a CSV as .csv (text/csv;charset=utf-8).
 * iPhone Safari often ignores that attribute on blob URLs, so CSV uses the
 * share sheet with a File named .csv and type text/csv.
 * The native app skips the Web Share API. WKWebView drops the .csv extension
 * for a text file, so native writes the file and opens the system share sheet.
 */

import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export function shareWasCancelled(error) {
  const name = error && error.name ? String(error.name) : '';
  const message = error && error.message ? String(error.message) : '';
  return /abort|cancel/i.test(`${name} ${message}`);
}

function safeFileName(filename) {
  const cleaned = String(filename || 'pacarion.txt').replace(/[^\w.-]+/g, '_');
  return cleaned || 'pacarion.txt';
}

async function shareWithCapacitor(filename, text) {
  const path = safeFileName(filename);
  await Filesystem.writeFile({
    path,
    data: text,
    directory: Directory.Cache,
    encoding: Encoding.UTF8,
  });
  const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
  await Share.share({
    title: filename,
    files: [uri],
    dialogTitle: filename,
  });
}

export const CSV_CONTENT_TYPE = 'text/csv;charset=utf-8';

function isCsvDownload(filename, mimeType) {
  const name = String(filename || '').toLowerCase();
  const type = String(mimeType || '').toLowerCase().split(';')[0].trim();
  return name.endsWith('.csv') || type === 'text/csv' || type === 'application/csv';
}

function csvFilename(filename) {
  const cleaned = safeFileName(filename || 'pacarion.csv');
  if (cleaned.toLowerCase().endsWith('.csv')) return cleaned;
  const stem = cleaned.replace(/\.(txt|text)$/i, '');
  return `${stem || 'pacarion'}.csv`;
}

function isIosBrowser() {
  if (typeof navigator === 'undefined' || Capacitor.isNativePlatform()) return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1;
}

function downloadWithAnchor(filename, text, mimeType) {
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Resolves after the share sheet returns or the browser download starts.
 * A cancelled share sheet rejects so callers can skip the success toast.
 */
export async function shareTextFile(filename, text, mimeType = 'text/plain') {
  const csv = isCsvDownload(filename, mimeType);
  const name = csv ? csvFilename(filename) : safeFileName(filename);
  const type = csv ? CSV_CONTENT_TYPE : (mimeType || 'text/plain');
  const body = String(text == null ? '' : text);

  if (Capacitor.isNativePlatform()) {
    await shareWithCapacitor(name, body);
    return 'shared';
  }

  if (csv && isIosBrowser()) {
    const file = typeof File !== 'undefined' ? new File([body], name, { type: 'text/csv' }) : null;
    const canShare = Boolean(
      file
      && typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [file] })
    );
    if (canShare) {
      try {
        await navigator.share({ files: [file], title: name });
        return 'shared';
      } catch (error) {
        if (shareWasCancelled(error)) throw error;
      }
    }
  }

  if (!csv && typeof navigator !== 'undefined' && typeof File !== 'undefined') {
    const file = new File([body], name, { type });
    const canShare = Boolean(
      navigator.share
      && navigator.canShare
      && navigator.canShare({ files: [file] })
    );
    if (canShare) {
      try {
        await navigator.share({ files: [file], title: name });
        return 'shared';
      } catch (error) {
        if (shareWasCancelled(error)) throw error;
      }
    }
  }

  downloadWithAnchor(name, body, type);
  return 'downloaded';
}

function shareableUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim());
    if (parsed.protocol === 'https:') return parsed.toString();
    const local = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (parsed.protocol === 'http:' && local) return parsed.toString();
  } catch (error) {
    return '';
  }
  return '';
}

/**
 * Share an https report link. The iOS app uses the share sheet.
 * A capacitor origin is rejected so the link stays a web address.
 */
export async function shareLink(url, title) {
  const target = shareableUrl(url);
  if (!target) throw new Error('Share links use an https address.');
  const name = title || 'Pacarion Home History Report';
  if (Capacitor.isNativePlatform()) {
    await Share.share({ title: name, url: target, dialogTitle: name });
    return 'shared';
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: name, url: target });
      return 'shared';
    } catch (error) {
      if (shareWasCancelled(error)) throw error;
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(target);
    return 'copied';
  }
  throw new Error('This browser could not share the link.');
}
